
import { Bell, Calendar, LogOut, Menu, Plus, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/app/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account.",
      });
      navigate("/");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Logout failed",
        description: "There was a problem logging you out. Please try again.",
      });
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <div className="container flex items-center justify-between h-16 px-4 mx-auto">
        {/* Logo & Search */}
        <div className="flex items-center gap-4">
          <h1 
            className="text-2xl font-bold cursor-pointer"
            onClick={() => navigate("/")}
          >
            fynd<span className="text-primary">tyme</span>
          </h1>
          <div className="hidden md:block">
            <Input
              placeholder="Search events or people..."
              className="w-64"
            />
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/calendar")}
          >
            <Calendar className="w-5 h-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/notifications")}
          >
            <Bell className="w-5 h-5" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="gap-1"
            onClick={() => navigate("/create-event")}
          >
            <Plus className="w-4 h-4" /> New Event
          </Button>
          
          {/* User dropdown with logout */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <User className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <Menu className="w-5 h-5" />
        </Button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200">
          <div className="p-4">
            <Input
              placeholder="Search events or people..."
              className="mb-4"
            />
            <div className="grid grid-cols-4 gap-2 mb-4">
              <Button 
                variant="ghost" 
                className="flex flex-col items-center gap-1 h-auto py-2"
                onClick={() => {
                  navigate("/calendar");
                  setIsMobileMenuOpen(false);
                }}
              >
                <Calendar className="w-5 h-5" />
                <span className="text-xs">Calendar</span>
              </Button>
              <Button 
                variant="ghost" 
                className="flex flex-col items-center gap-1 h-auto py-2"
                onClick={() => {
                  navigate("/notifications");
                  setIsMobileMenuOpen(false);
                }}
              >
                <Bell className="w-5 h-5" />
                <span className="text-xs">Alerts</span>
              </Button>
              <Button 
                variant="ghost" 
                className="flex flex-col items-center gap-1 h-auto py-2"
                onClick={() => {
                  navigate("/create-event");
                  setIsMobileMenuOpen(false);
                }}
              >
                <Plus className="w-5 h-5" />
                <span className="text-xs">Create</span>
              </Button>
              <Button 
                variant="ghost" 
                className="flex flex-col items-center gap-1 h-auto py-2"
                onClick={() => {
                  navigate("/profile");
                  setIsMobileMenuOpen(false);
                }}
              >
                <User className="w-5 h-5" />
                <span className="text-xs">Profile</span>
              </Button>
            </div>
            
            {/* Mobile logout button */}
            <Button 
              variant="outline" 
              className="w-full flex items-center justify-center text-red-600 border-red-200"
              onClick={() => {
                handleLogout();
                setIsMobileMenuOpen(false);
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
